import type { ComponentPropsWithoutRef, FunctionComponent } from 'react'
import React from 'react'

import classnames from 'classnames'

import { Accordion, AccordionItem } from '../Accordion'
import { Follow } from '../Follow'
import { Hr } from '../Hr'
import type { SocialIconName } from '../Links'
import { NavigationLink } from '../Links/NavigationLink'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './LocalFooter.module.scss'

export const LocalFooter: FunctionComponent<Props> = ({
  className,
  strapline,
  accordionProps,
  followLinks,
  ...rest
}) => (
  <footer
    className={classnames(styles.footerWrapper, className)}
    data-testid="local-footer-wrapper"
    {...rest}
  >
    <div
      className={
        followLinks ? styles.footerAccordionWrapper : styles.footerFullWidth
      }
    >
      {strapline && (
        <>
          <Hr className="sb-margin-top-zero sb-margin-bottom-3" />
          <TypographyV2
            className="sb-margin-bottom-3"
            data-testid="footer-about-us"
            textStyle="sb-text-heading-large"
          >
            About Us
          </TypographyV2>
          <TypographyV2
            className={classnames(
              accordionProps && 'sb-margin-bottom-4',
              styles.footerStrapline
            )}
            data-testid="local-footer-strapline"
            dangerouslySetInnerHTML={{ __html: strapline }}
            tag="span"
            textStyle="sb-text-body-default-regular"
          />
        </>
      )}
      {accordionProps && (
        <Accordion data-testid="local-footer-accordion">
          {accordionProps.map(({ label, links }) => (
            <AccordionItem
              key={label}
              label={label}
              data-testid="local-footer-accordion-section"
              content={
                <ul className={styles.linksList}>
                  {links.map(
                    ({ text, href }, index) =>
                      text &&
                      href && (
                        <li key={`${text}-${index}`}>
                          <NavigationLink
                            key={`${text} ${index}`}
                            href={href}
                            linkStyle="secondary"
                            className={`sb-margin-bottom-${
                              index < links.length - 1 ? 3 : 4
                            }`}
                            data-testid="local-footer-accordion-section-link"
                          >
                            {text}
                          </NavigationLink>
                        </li>
                      )
                  )}
                </ul>
              }
            />
          ))}
        </Accordion>
      )}
    </div>
    {followLinks && (
      <Follow
        className={styles.footerFollowLinks}
        links={followLinks}
        location="bottom"
        data-testid="local-footer-follow-links"
      />
    )}
  </footer>
)

///////// IMPLEMENTATION /////////

type Props = {
  /**
   * Optional class name to add extra styling to the component
   */
  className?: string
  /**
   * Text that describes the section of the website the footer is on
   */
  strapline?: string
  /**
   * Array of objects, each consisting of an accordion label text string and an array of link objects, each with a href and text, which will be rendered as an Accordion
   */
  accordionProps?: Array<{
    label: string
    links: Array<{
      text: string
      href: string
    }>
  }>
  /**
   * Array of social media following link objects, each consisting of an icon and href strings, plus a linkText string to be rendered as innerHTML
   */
  followLinks?: Array<{
    icon: SocialIconName
    href: string
    linkText: string
  }>
} & ComponentPropsWithoutRef<'footer'>
